// useAutoSave — watches the Zustand project and debounces a PUT to D1.
// Fires 1 second after the last mutation. Uses an upsert (PUT), so it handles
// both new projects and updates without a separate POST flow.

import { useEffect, useRef } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useUiStore } from '../store/uiStore';

const DEBOUNCE_MS = 1000;

export function useAutoSave() {
  const project = useProjectStore(st => st.project);
  const setIsSaving = useUiStore(st => st.setIsSaving);
  const setLastSaved = useUiStore(st => st.setLastSaved);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!project) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsSaving(true);
      fetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project),
      })
        .then(res => { if (res.ok) setLastSaved(new Date().toISOString()); })
        .catch(() => { /* silent — user still edits; will retry on next mutation */ })
        .finally(() => setIsSaving(false));
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [project, setIsSaving, setLastSaved]);
}
